import { useState } from 'react';
import { Phone, MessageCircle, Mail, Linkedin, RefreshCw, Info, ChevronRight, Loader2, AlertTriangle, Calendar, CreditCard, Users } from 'lucide-react';
import { PlanDetailsModal } from './PlanDetailsModal';
import { ProcessedLeadModal } from './ProcessedLeadModal';
import { useSubscription } from '../../hooks/useSubscription';
import type { PlanFeatures } from '../../types/subscription';
import { format } from 'date-fns';

export function SubscriptionSettings() {
  const { usageData, plansByTier, availableTerms, allPlans, isLoading, error } = useSubscription();
  const [selectedPlanFeatures, setSelectedPlanFeatures] = useState<{ basePlan: string; features: PlanFeatures } | null>(null);
  const [showProcessedLeadModal, setShowProcessedLeadModal] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState<number | null>(null);

  const activeTerm = selectedTerm ?? (usageData?.plan?.prepaid_months || availableTerms[0] || 2);

  if (isLoading) {
    return (
      <div className="max-w-6xl flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-[#1875FF]" />
      </div>
    );
  }

  if (error || !usageData?.has_subscription) {
    return (
      <div className="max-w-6xl">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-amber-900">No Active Subscription</h3>
            <p className="text-sm text-amber-700 mt-1">
              {error || 'Please contact support to set up your subscription.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { plan, billing_period, overage } = usageData;
  const currentBasePlan = plan.base_plan;
  const currentPrepaidMonths = plan.prepaid_months;

  const getTermLabel = (months: number) => `${months} Months`;

  const getTermDiscount = (months: number) => {
    const anyPlan = allPlans.find(p => p.prepaid_months === months);
    return anyPlan?.discount_percent || 0;
  };

  const getButtonType = (tierBasePlan: string): 'downgrade' | 'current' | 'upgrade' => {
    const tierOrder = ['START', 'GROW', 'SCALE'];
    const currentIdx = tierOrder.indexOf(currentBasePlan);
    const targetIdx = tierOrder.indexOf(tierBasePlan);
    if (currentIdx === targetIdx) return 'current';
    if (targetIdx < currentIdx) return 'downgrade';
    return 'upgrade';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
  };

  const periodEndDate = format(new Date(billing_period.period_end), 'MMMM d, yyyy');
  const prepaidEndDate = billing_period.prepaid_end ? format(new Date(billing_period.prepaid_end), 'MMMM d, yyyy') : null;
  const startDate = billing_period.started_at ? format(new Date(billing_period.started_at), 'MMMM d, yyyy') : '--';
  const hasOverage = overage.total_overage_cost > 0;

  const handleScrollToChoosePlan = () => {
    const el = document.getElementById('choose-plan');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="max-w-6xl space-y-6">
      <div className="mb-6 flex items-center gap-3">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-xl flex items-center justify-center shadow-md">
          <CreditCard className="w-6 h-6 text-[#1875FF]" />
        </div>
        <div>
          <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display">Subscription</h2>
          <p className="text-gray-600">Manage your subscription plan and billing cycle</p>
        </div>
      </div>

      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:border-[#05E0D1]">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-lg font-extrabold text-gray-900 font-display">Current Plan</h3>
            <p className="text-sm text-gray-600 mt-1">
              You are currently on the <span className="font-semibold">{currentBasePlan}</span> plan
              <span className="mx-1 text-gray-400">--</span>
              <span className="font-medium">{currentPrepaidMonths} Months Prepaid</span>
              {plan.discount_percent > 0 && (
                <span className="ml-1.5 text-xs font-bold text-[#05E0D1]">-{plan.discount_percent}%</span>
              )}
            </p>
          </div>
          <div className="px-4 py-2 bg-gradient-to-r from-[#1875FF]/10 to-[#05E0D1]/10 text-[#1875FF] rounded-lg font-medium">
            {currentBasePlan}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="p-4 bg-white/80 rounded-lg border border-gray-100">
            <div className="text-sm text-gray-600">Price Per Lead</div>
            <div className="text-lg font-extrabold text-gray-900 font-display mt-1">${Number(plan.price_per_lead).toFixed(1)}</div>
          </div>
          <div className="p-4 bg-white/80 rounded-lg border border-gray-100">
            <div className="text-sm text-gray-600 flex items-center gap-1">
              <Users className="w-3.5 h-3.5" />
              Leads/Month
            </div>
            <div className="text-lg font-extrabold text-gray-900 font-display mt-1">{plan.leads_included.toLocaleString()}</div>
          </div>
          <div className="p-4 bg-white/80 rounded-lg border border-gray-100">
            <div className="text-sm text-gray-600 flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              Start Date
            </div>
            <div className="text-lg font-extrabold text-gray-900 font-display mt-1">{startDate}</div>
          </div>
          <div className="p-4 bg-white/80 rounded-lg border border-gray-100">
            <div className="text-sm text-gray-600">Next Billing Date</div>
            <div className="text-lg font-extrabold text-gray-900 font-display mt-1">{prepaidEndDate || periodEndDate}</div>
          </div>
          <div className="p-4 bg-white/80 rounded-lg border border-gray-100">
            <div className="text-sm text-gray-600 flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              Prepaid Until
            </div>
            <div className="text-lg font-extrabold text-gray-900 font-display mt-1">
              {prepaidEndDate || '--'}
            </div>
          </div>
        </div>

        {hasOverage && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-semibold text-amber-900">Current Period Overage</span>
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-extrabold text-amber-900 font-display">
                {formatCurrency(overage.total_overage_cost)}
              </span>
              <span className="text-sm text-amber-700">in additional charges this period</span>
            </div>
            <p className="text-xs text-amber-600 mt-1">
              Estimated total: {formatCurrency(usageData.estimated_total)}
            </p>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={handleScrollToChoosePlan}
            className="px-4 py-2 text-sm font-medium text-[#1875FF] hover:bg-[#1875FF]/5 rounded-lg transition-colors"
          >
            Change Plan
          </button>
        </div>
      </div>

      <div id="choose-plan" className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:border-[#05E0D1] scroll-mt-6">
        <div className="flex items-center justify-between mb-8">
          <h3 className="text-3xl font-extrabold text-gray-900 tracking-tight font-display">Choose Your Plan</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plansByTier.map((tier) => {
            const p = tier.plans[activeTerm];
            if (!p) return null;

            const buttonType = getButtonType(tier.base_plan);
            const isCurrent = buttonType === 'current' && activeTerm === currentPrepaidMonths;
            const isCurrentTier = buttonType === 'current';

            return (
              <div key={tier.base_plan} className="relative rounded-2xl transition-all bg-white">
                {isCurrentTier ? (
                  <div className="absolute inset-0 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] rounded-2xl p-[2px]">
                    <div className="w-full h-full bg-white rounded-2xl"></div>
                  </div>
                ) : null}

                <div className={`relative h-full flex flex-col p-4 rounded-2xl ${!isCurrentTier ? 'border-2 border-gray-200 hover:border-gray-300' : ''}`}>
                  <div className="text-center mb-4">
                    <h4 className="text-2xl font-extrabold text-[#05E0D1] font-display mb-3">{tier.base_plan}</h4>
                    <div className="text-3xl font-bold text-gray-900 mb-2 font-metric">
                      {p.leads_included.toLocaleString()}
                    </div>
                    <div className="flex items-center justify-center gap-1 text-gray-600">
                      <span>processed leads/month</span>
                      <button
                        onClick={() => setShowProcessedLeadModal(true)}
                        className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                      >
                        <Info className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                  </div>

                  <div className="mb-4 flex-grow">
                    <h5 className="text-lg font-extrabold text-[#05E0D1] mb-3 font-display">Monthly Actions</h5>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-[#05E0D1]" />
                          <span className="text-gray-700">Calls</span>
                        </div>
                        <span className="text-gray-900 font-semibold">{p.calls_included.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <MessageCircle className="w-4 h-4 text-[#05E0D1]" />
                          <span className="text-gray-700">WhatsApp Msgs</span>
                        </div>
                        <span className="text-gray-900 font-semibold">{p.whatsapp_included.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-[#05E0D1]" />
                          <span className="text-gray-700">Emails</span>
                        </div>
                        <span className="text-gray-900 font-semibold">{p.emails_included.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Linkedin className="w-4 h-4 text-[#05E0D1]" />
                          <span className="text-gray-700">LinkedIn</span>
                        </div>
                        <span className="text-gray-900 font-semibold">{p.linkedin_included.toLocaleString()}</span>
                      </div>
                      {p.followups_included != null ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <RefreshCw className="w-4 h-4 text-[#05E0D1]" />
                            <span className="text-gray-700">Follow-Ups</span>
                          </div>
                          <span className="text-gray-900 font-semibold">{p.followups_included}</span>
                        </div>
                      ) : (
                        <div className="h-[28px]"></div>
                      )}
                    </div>
                  </div>

                  <div className="mt-auto">
                    <button
                      onClick={() => setSelectedPlanFeatures({ basePlan: tier.base_plan, features: p.features })}
                      className="flex items-center justify-center gap-1 text-[#05E0D1] font-semibold mb-4 hover:gap-2 transition-all mx-auto"
                    >
                      View All Features <ChevronRight className="w-4 h-4" />
                    </button>

                    <div className="text-center pt-3 border-t border-gray-200">
                      <div className="mb-3">
                        <span className="text-3xl font-extrabold text-[#05E0D1] font-display">
                          ${Number(p.price_per_lead).toFixed(1)}
                        </span>
                        <span className="text-sm text-gray-600 ml-1">per lead</span>
                      </div>

                      {isCurrent ? (
                        <button
                          disabled
                          className="w-full py-2.5 px-4 bg-gray-200 text-gray-500 font-semibold rounded-lg cursor-not-allowed"
                        >
                          CURRENT PLAN
                        </button>
                      ) : buttonType === 'downgrade' ? (
                        <div className="relative w-full">
                          <div className="absolute inset-0 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] rounded-lg p-[2px]">
                            <div className="w-full h-full bg-white rounded-lg"></div>
                          </div>
                          <button className="relative w-full py-2.5 px-4 font-semibold rounded-lg transition-all hover:shadow-md">
                            <span className="bg-gradient-to-r from-[#1875FF] to-[#05E0D1] bg-clip-text text-transparent">
                              DOWNGRADE
                            </span>
                          </button>
                        </div>
                      ) : (
                        <button className="w-full py-2.5 px-4 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white font-semibold rounded-lg transition-all hover:shadow-lg">
                          UPGRADE
                        </button>
                      )}
                    </div>
                  </div>

                  {isCurrentTier && (
                    <div className="absolute top-4 right-4">
                      <div className="px-3 py-1 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white text-xs font-bold rounded-full">
                        Current
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {availableTerms.length > 1 && (
          <div className="flex justify-center mt-8">
            <div className="inline-flex items-center bg-gray-100 rounded-full p-1.5 gap-1">
              {availableTerms.map((term) => {
                const isActive = term === activeTerm;
                const isSubscribedTerm = term === currentPrepaidMonths;
                const discount = getTermDiscount(term);

                return (
                  <button
                    key={term}
                    onClick={() => setSelectedTerm(term)}
                    className={`
                      relative px-5 py-2.5 rounded-full text-sm font-semibold transition-all flex items-center gap-2
                      ${isActive
                        ? 'bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white shadow-md'
                        : isSubscribedTerm
                          ? 'bg-white text-gray-900 shadow-sm ring-2 ring-[#05E0D1]/40'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-white/60'
                      }
                    `}
                  >
                    {getTermLabel(term)}
                    {discount > 0 && (
                      <span className={`
                        text-xs font-bold px-1.5 py-0.5 rounded-full
                        ${isActive
                          ? 'bg-white/20 text-white'
                          : 'bg-[#05E0D1]/10 text-[#05E0D1]'
                        }
                      `}>
                        -{discount}%
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <PlanDetailsModal
        isOpen={selectedPlanFeatures !== null}
        onClose={() => setSelectedPlanFeatures(null)}
        planName={selectedPlanFeatures?.basePlan || ''}
        features={selectedPlanFeatures?.features ? {
          leads: plansByTier.find(t => t.base_plan === selectedPlanFeatures.basePlan)?.plans[activeTerm]?.leads_included || 0,
          calls: plansByTier.find(t => t.base_plan === selectedPlanFeatures.basePlan)?.plans[activeTerm]?.calls_included || 0,
          whatsapp: plansByTier.find(t => t.base_plan === selectedPlanFeatures.basePlan)?.plans[activeTerm]?.whatsapp_included || 0,
          emails: plansByTier.find(t => t.base_plan === selectedPlanFeatures.basePlan)?.plans[activeTerm]?.emails_included || 0,
          linkedin: plansByTier.find(t => t.base_plan === selectedPlanFeatures.basePlan)?.plans[activeTerm]?.linkedin_included || 0,
          followUps: plansByTier.find(t => t.base_plan === selectedPlanFeatures.basePlan)?.plans[activeTerm]?.followups_included ?? undefined,
          ...selectedPlanFeatures.features,
        } : {} as Record<string, never>}
      />

      <ProcessedLeadModal
        isOpen={showProcessedLeadModal}
        onClose={() => setShowProcessedLeadModal(false)}
      />
    </div>
  );
}
